import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import MobileAvatar from "@Components/mobile/common/mobile-avatar";
import { Box, CardActionArea, Skeleton, Stack, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material";
import { BybListItemBackgroundColorGradient } from "@Shared/types/list-item-types";
import { isNumber, isString } from "lodash-es";
import React from "react";
import { AvatarUser } from "../base-avatar";
import BybAvatar from "../byb-avatar";
import BybSticker from "../byb-sticker";

enum BybListItemType {
  AVATAR = "avatar",
  ICON = "icon",
}

interface IBybListItem {
  id?: string;
  header: string | React.ReactNode;
  subHeader?: string | React.ReactNode;
  listMarker?: string | number | React.ReactNode;
  iconType?: `${BybListItemType}`;
  value?: string | number | React.ReactNode;
  topRightSticker?: React.ReactNode;
  topLeftSticker?: React.ReactNode;
  bottomRightSticker?: React.ReactNode;
  bottomLeftSticker?: React.ReactNode;
  backgroundColorGradient?: BybListItemBackgroundColorGradient;
  imgSrc?: string | null;
  icon?: React.ReactNode;
  user?: AvatarUser;
  disabled?: boolean;
  disableGutters?: boolean;
  textAlign?: "left" | "center" | "right";
  loading?: boolean;
  highlight?: boolean;
  onClick?: () => void;
}

const BybListItem = ({
  id,
  header,
  subHeader,
  listMarker,
  iconType = BybListItemType.ICON,
  value,
  topRightSticker,
  topLeftSticker,
  bottomRightSticker,
  bottomLeftSticker,
  backgroundColorGradient,
  imgSrc,
  icon,
  user,
  disabled = false,
  disableGutters = false,
  textAlign = "left",
  onClick,
  loading,
  highlight,
}: IBybListItem) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ListItemWrapper
      id={id}
      onClick={onClick}
      disabled={disabled}
      sx={{
        paddingX: theme.spacing(disableGutters ? 0 : 2.5),
        paddingY: theme.spacing(1.25),
        backgroundColor: highlight ? theme.palette.dsSurface.v200 : "transparent",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center", textAlign: "center" }}>
          {listMarker && (
            <Box sx={{ width: theme.spacing(3.5) }}>
              {isString(listMarker) || isNumber(listMarker) ? (
                <Typography
                  variant="textXs500"
                  sx={{
                    color: "dsBase.v200",
                    fontSize: "inherit",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transform: "scale(var(--text-scale, 1))",
                    transformOrigin: "left center",
                  }}
                >
                  {listMarker}
                </Typography>
              ) : (
                listMarker
              )}
            </Box>
          )}
          <Box sx={{ position: "relative" }}>
            {iconType === BybListItemType.AVATAR ? (
              isSmDown ? (
                <MobileAvatar src={imgSrc || ""} user={user} size={2} />
              ) : (
                <BybAvatar src={imgSrc || ""} user={user} />
              )
            ) : (
              <Box
                sx={{
                  position: "relative",
                  height: theme.spacing(6),
                  width: theme.spacing(6),
                  borderRadius: "16px",
                  ...(imgSrc
                    ? {
                        background: `url(${imgSrc})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : {
                        background: backgroundColorGradient ? theme.palette.dsPrimaryGradient[backgroundColorGradient] : theme.palette.dsSurface.v100,
                      }),
                }}
              >
                {icon && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: disabled ? "dsNeutral.v200" : "dsNeutral.v400",
                      borderRadius: theme.spacing(2),
                    }}
                  >
                    {icon}
                  </Box>
                )}
              </Box>
            )}

            {topRightSticker && (
              <BybSticker size="small" anchor="top-right" margin={-0.25}>
                {topRightSticker}
              </BybSticker>
            )}
            {topLeftSticker && (
              <BybSticker size="small" anchor="top-left" margin={-0.25}>
                {topLeftSticker}
              </BybSticker>
            )}
            {bottomRightSticker && (
              <BybSticker size="small" anchor="bottom-right" margin={-0.25}>
                {bottomRightSticker}
              </BybSticker>
            )}
            {bottomLeftSticker && (
              <BybSticker size="small" anchor="bottom-left" margin={-0.25}>
                {bottomLeftSticker}
              </BybSticker>
            )}
          </Box>
        </Stack>
        <Stack sx={{ flexGrow: 1, textAlign, overflow: "hidden" }}>
          {loading ? (
            <>
              <Skeleton variant="text" width="40%" height={20} />
              {subHeader && <Skeleton variant="text" width="40%" height={20} />}
            </>
          ) : (
            <>
              {isString(header) ? (
                <Typography
                  variant={subHeader ? "textBase500" : "textBase400"}
                  color={disabled ? "dsNeutral.v200" : "dsBase.v200"}
                  textTransform={"capitalize"}
                >
                  {header}
                </Typography>
              ) : (
                header
              )}
              {subHeader &&
                (isString(subHeader) ? (
                  <Typography variant="textS500" color={disabled ? "dsNeutral.v200" : "dsNeutral.v300"}>
                    {subHeader}
                  </Typography>
                ) : (
                  subHeader
                ))}
            </>
          )}
        </Stack>
        {loading ? (
          <Skeleton variant="text" width={40} height={20} />
        ) : (
          value &&
          (isString(value) || isNumber(value) ? (
            <Typography variant="textBase400" color={disabled ? "dsNeutral.v200" : "dsBase.v200"}>
              {value}
            </Typography>
          ) : (
            value
          ))
        )}
      </Stack>
    </ListItemWrapper>
  );
};

const ListItemWrapper = ({
  id,
  onClick,
  children,
  sx,
  disabled,
}: {
  id?: string;
  children: React.ReactNode;
  sx: SxProps;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  return !!onClick ? (
    <HapticsButtonWrapper>
      <CardActionArea id={id} onClick={onClick} sx={sx} disabled={disabled}>
        {children}
      </CardActionArea>
    </HapticsButtonWrapper>
  ) : (
    <Box id={id} sx={sx}>
      {children}
    </Box>
  );
};

export default BybListItem;
